import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { RenderAccordionContentParams } from '@components/settings/components/common/animatedTable';
import { isWeb } from '@helpers';
import NoData from '@components/settings/components/common/animatedTable/components/NoData';
import ColumnCell from '@components/settings/components/common/animatedTable/components/ColumnCell';
import TouchableElement from '@components/common/touchableElement';
import { MachineEventSearchItem } from '@interfaces/machines';
import { RawMachineEventData } from '@interfaces/base';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface AccordionData {
  traceEvents?: MachineEventSearchItem[];
  rawMachineEvents?: Record<number, RawMachineEventData> | null;
}

export const getAccordionItemsCount = (accordionData: AccordionData): number => {
  const traceEvents = accordionData?.traceEvents || [];
  return traceEvents.length;
};

export const renderAccordionContent = (params: RenderAccordionContentParams): React.ReactNode => {
  const { accordionData, C, scale, font, lineSize, containerWidth, config, cellRenderer, valuesAlreadyTranslated, onSubRowPress, onSubRowPressIn, selectedItem } =
    params;

  if (!accordionData) {
    return (
      <View style={{ minHeight: scale(lineSize) }}>
        <NoData C={C} loaded font={font} scale={scale} lineSize={lineSize} />
      </View>
    );
  }

  const data = accordionData as AccordionData;
  const traceEvents = data.traceEvents || [];

  if (traceEvents.length === 0) {
    return (
      <View style={{ minHeight: scale(lineSize) }}>
        <NoData C={C} loaded font={font} scale={scale} lineSize={lineSize} />
      </View>
    );
  }

  return traceEvents.map((traceEvent, subIndex: number) => {
    const sequenceNumber = traceEvent.sequenceNumber;
    const rawMachineEventData = sequenceNumber != null ? data.rawMachineEvents?.[sequenceNumber] : null;

    // Create subrow item with marker for column config
    const subRowItem = {
      ...traceEvent,
      rawMachineEventData,
      __isSubRowForColumnConfig: true,
    };

    const subRowConfig = typeof config === 'function' ? config(subRowItem) : config;
    const isLastRow = subIndex === traceEvents.length - 1;

    // Check if this subrow should have overlay (when another subrow is selected)
    const isSelectedSubRow =
      selectedItem != null && selectedItem?.__isSubRowForColumnConfig && selectedItem?.sequenceNumber === sequenceNumber;
    const hasSubRowOverlay = selectedItem != null && selectedItem?.__isSubRowForColumnConfig && !isSelectedSubRow;

    return (
      <TouchableElement
        key={`subrow-${sequenceNumber}-${subIndex}`}
        onPress={() => onSubRowPress?.(subRowItem)}
        onPressIn={onSubRowPressIn ? () => onSubRowPressIn(subRowItem) : undefined}
        hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
        disabled={!onSubRowPress}
        testID={sequenceNumber != null ? TEST_IDS.ncFileBatchEvents.subRow(sequenceNumber) : undefined}
      >
        {({ boxStyle: subRowBoxStyle, pressHoverColor: subRowPressHoverColor }) => (
          <View
            style={[
              {
                minHeight: scale(lineSize),
                borderBottomWidth: isLastRow ? 0 : StyleSheet.hairlineWidth,
                borderBottomColor: isLastRow ? 'transparent' : C('Hairline/Light/No trans'),
                backgroundColor: C(subRowPressHoverColor('Background/Light/Event Trace')),
                position: 'relative',
              },
              subRowBoxStyle,
            ]}
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[
                {
                  paddingRight: scale(30),
                  paddingLeft: scale(15),
                  flexDirection: 'row',
                  alignItems: 'center',
                },
                isWeb && { width: '100%' },
              ]}
            >
              {subRowConfig.map((column, configIndex: number) => (
                <ColumnCell
                  key={`subrow-config-${column.type || column.key}-${configIndex}`}
                  column={column}
                  item={subRowItem}
                  configIndex={configIndex}
                  containerWidth={containerWidth}
                  C={C}
                  font={font}
                  scale={scale}
                  cellRenderer={cellRenderer}
                  valuesAlreadyTranslated={valuesAlreadyTranslated}
                />
              ))}
            </ScrollView>
            {hasSubRowOverlay && (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: C('Overlay/Light/Soften Strong'),
                  pointerEvents: 'none',
                }}
              />
            )}
          </View>
        )}
      </TouchableElement>
    );
  });
};
